import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WhatsAppButton = () => {
  const { isArabic } = useLanguage();
  const [whatsappNumber, setWhatsappNumber] = useState('0943999339');
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Fetch WhatsApp number from settings
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API}/settings`);
        const data = await res.json();
        if (data.whatsappNumber) {
          setWhatsappNumber(data.whatsappNumber);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };
    
    fetchSettings();
    
    // Show button after a short delay
    const timer = setTimeout(() => setIsVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Format number for WhatsApp link (remove spaces and leading zeros, add country code)
  const formatWhatsAppNumber = (num) => {
    let cleaned = num.replace(/\s+/g, '').replace(/^0+/, '');
    // Add Libya country code if not present
    if (!cleaned.startsWith('218')) {
      cleaned = '218' + cleaned;
    }
    return cleaned;
  };

  const whatsappLink = `https://wa.me/${formatWhatsAppNumber(whatsappNumber)}?text=${encodeURIComponent(
    isArabic 
      ? 'مرحباً، أريد الاستفسار عن منتجاتكم الطبية'
      : 'Hello, I would like to inquire about your medical products'
  )}`;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          className="fixed bottom-6 left-6 z-50"
          data-testid="whatsapp-button"
        >
          {/* Tooltip - positioned to the right of the button */}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                className="absolute bottom-0 left-full ml-3 bg-white rounded-xl shadow-lg p-4 w-64"
              >
                <button 
                  onClick={() => setShowTooltip(false)}
                  className="absolute top-2 right-2 p-1 hover:bg-slate-100 rounded-full"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
                <p className="text-sm text-slate-700 font-medium mb-2">
                  {isArabic ? 'تواصل معنا عبر الواتساب' : 'Chat with us on WhatsApp'}
                </p>
                <p className="text-xs text-slate-500">
                  {isArabic 
                    ? 'نرد على استفساراتكم بسرعة!'
                    : 'We respond quickly to your inquiries!'
                  }
                </p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* WhatsApp Button */}
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className="group flex items-center justify-center w-16 h-16 bg-green-500 rounded-full shadow-lg hover:shadow-xl hover:bg-green-600 transition-all duration-300"
          >
            {/* Pulse animation */}
            <span className="absolute w-full h-full rounded-full bg-green-500 animate-ping opacity-20" />
            
            {/* WhatsApp Icon */}
            <svg 
              className="w-8 h-8 text-white relative z-10" 
              viewBox="0 0 24 24" 
              fill="currentColor"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppButton;
